/**
 * Collapsed merged <d-ref|d-src data-ref-collapsed="1"> — compact toggle + expanded sources.
 * Terminal plain <d-src> (no data-ref-collapsed): one Bootstrap-icon toggle; several <d-src> in a row
 * (only whitespace between) share one icon. "Terminal" = the reference closes its text run: nothing but
 * whitespace / further plain <d-src> follows before a boundary (end of the parent, <br>, <img>, <d-caption>,
 * a block element, <d-sep>/<d-num>, a trailing annotation tag such as <d-small>/<d-abbr>/<d-phr>/<d-ref>),
 * or the next text starts a new sense ("2. ", bullet) or an annotation ("[...]", "(...)").
 * A d-src with running text after it ("(see X) and more") stays visible. Not only the LAST d-src of a parent:
 * a reference before a picture or before the next numbered <div> sense collapses too (Slava, Slovary 1312/1315).
 */
(function() {
  'use strict';

  function isWhitespaceTextNode(n) {
    return n && n.nodeType === 3 && (!n.nodeValue || !/[^\s\u00A0]/.test(n.nodeValue));
  }

  function isPlainDsrc(el) {
    if (!el || el.nodeType !== 1 || !el.tagName) {
      return false;
    }
    if (el.tagName.toLowerCase() !== 'd-src') {
      return false;
    }
    return !el.hasAttribute('data-ref-collapsed');
  }

  /** Tags after which a reference is "terminal" (the text run it belongs to is over). */
  var BOUNDARY_TAGS = {
    BR: 1, IMG: 1, HR: 1, DIV: 1, P: 1, UL: 1, OL: 1, LI: 1, TABLE: 1, TR: 1, FIGURE: 1, BLOCKQUOTE: 1,
    H1: 1, H2: 1, H3: 1, H4: 1, H5: 1, H6: 1,
    'D-BLOCK': 1, 'D-CAPTION': 1, 'D-NUM': 1, 'D-SEP': 1, 'D-TERM-VAR': 1,
    'D-SMALL': 1, 'D-ABBR': 1, 'D-PHR': 1, 'D-REF': 1, 'D-NOTE': 1, 'D-SYN': 1, 'D-EX': 1, 'D-ETYM': 1, 'D-MARK': 1
  };
  /** Text that opens a new sense ("2. ", "3) ", bullet) or a trailing annotation ("[...]", "(...)"). */
  var TEXT_BOUNDARY_RE = /^[\s ]*(?:\d+[.)]|[•‣●\-]|\[|\()/;

  /** Does the text run end right after this plain d-src (see file header)? */
  function isTerminalDsrc(el) {
    var cur = el;
    while (cur) {
      var n = cur.nextSibling;
      while (n) {
        if (n.nodeType === 8 || isWhitespaceTextNode(n) || isPlainDsrc(n)) {
          n = n.nextSibling;
          continue;
        }
        if (n.nodeType === 3) {
          return TEXT_BOUNDARY_RE.test(n.nodeValue || '');
        }
        return !!(n.nodeType === 1 && BOUNDARY_TAGS[n.tagName]);
      }
      var par = cur.parentNode;
      if (!par || par.nodeType !== 1 || !par.parentNode || BOUNDARY_TAGS[par.tagName]) {
        return true;
      }
      cur = par; /* inline wrapper (<b>, <i>, <span>…): look at what follows it */
    }
    return true;
  }

  /** The plain d-src siblings that follow `first` (whitespace only between), `first` included. */
  function collectPlainDsrcRun(first) {
    var run = [first];
    var n = first.nextSibling;
    while (n) {
      if (n.nodeType === 8 || isWhitespaceTextNode(n)) {
        n = n.nextSibling;
        continue;
      }
      if (isPlainDsrc(n)) {
        run.push(n);
        n = n.nextSibling;
        continue;
      }
      break;
    }
    return run;
  }

  function removeWhitespaceBetween(parent, a, b) {
    var walk = a.nextSibling;
    while (walk && walk !== b) {
      var next = walk.nextSibling;
      if (isWhitespaceTextNode(walk)) {
        parent.removeChild(walk);
      }
      walk = next;
    }
  }

  function wrapTrailingDsrcCluster(parent, cluster) {
    for (var c = 0; c < cluster.length - 1; c++) {
      removeWhitespaceBetween(parent, cluster[c], cluster[c + 1]);
    }
    var wrap = document.createElement('span');
    wrap.className = 'd-src-cluster';
    wrap.setAttribute('data-d-src-cluster', '1');
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'd-src-cluster-toggle';
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-label', 'Sources');
    btn.setAttribute('title', 'Show sources');
    btn.innerHTML = '<i class="bi bi-journal-text d-src-cluster-icon" aria-hidden="true"></i>';
    var body = document.createElement('span');
    body.className = 'd-src-cluster-body';
    body.setAttribute('aria-hidden', 'true');
    var first = cluster[0];
    parent.insertBefore(wrap, first);
    wrap.appendChild(btn);
    wrap.appendChild(body);
    for (var i = 0; i < cluster.length; i++) {
      body.appendChild(cluster[i]);
    }
    btn.addEventListener('click', function(ev) {
      ev.preventDefault();
      ev.stopPropagation();
      var open = wrap.classList.toggle('is-expanded');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      body.setAttribute('aria-hidden', open ? 'false' : 'true');
    });
  }

  function initTrailingDsrcClusters(root) {
    if (!root || typeof root.querySelectorAll !== 'function') {
      return;
    }
    var list = root.querySelectorAll('d-src');
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      if (!isPlainDsrc(el) || el.closest('.d-src-cluster') || !el.parentNode) {
        continue;
      }
      if (!isTerminalDsrc(el)) {
        continue;
      }
      /* document order: `el` is the first d-src of its run (an earlier one would have wrapped it already) */
      wrapTrailingDsrcCluster(el.parentNode, collectPlainDsrcRun(el));
    }
  }

  function initCollapsedRefs(root) {
    if (!root || typeof root.querySelectorAll !== 'function') {
      return;
    }
    var nodes = root.querySelectorAll('d-ref[data-ref-collapsed="1"]:not([data-ref-collapse-inited]),d-src[data-ref-collapsed="1"]:not([data-ref-collapse-inited])');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      el.setAttribute('data-ref-collapse-inited', '1');
      var html = el.innerHTML;
      el.innerHTML = '';
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'd-ref-collapsed-toggle';
      btn.setAttribute('aria-expanded', 'false');
      btn.setAttribute('aria-label', 'Merged sources');
      btn.setAttribute('title', 'Show merged sources');
      btn.innerHTML = '<svg class="d-ref-collapsed-icon" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v11A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5v-11zm8 0A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v11a1.5 1.5 0 0 1-1.5 1.5h-3a1.5 1.5 0 0 1-1.5-1.5v-11z"/></svg>';
      var span = document.createElement('span');
      span.className = 'd-ref-collapsed-inner';
      span.innerHTML = html;
      span.setAttribute('aria-hidden', 'true');
      el.appendChild(btn);
      el.appendChild(span);
      (function(host, toggle, innerEl) {
        toggle.addEventListener('click', function(ev) {
          ev.preventDefault();
          ev.stopPropagation();
          var open = host.classList.toggle('is-expanded');
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          innerEl.setAttribute('aria-hidden', open ? 'false' : 'true');
        });
      })(el, btn, span);
    }
  }

  function init(root) {
    initCollapsedRefs(root);
    initTrailingDsrcClusters(root);
  }

  window.SLOVARY_REF_COLLAPSED = {
    init: init,
    initDataRefCollapsed: initCollapsedRefs,
    initTrailingDsrcClusters: initTrailingDsrcClusters,
  };
})();
